/**
 * Judgment Selling polar tradeoffs (SSOT §8) — Soft≪AA failure modes.
 * Extremes ≠ combat coeff / HP / speedMul. Visible judgment differences only.
 * Claim B chase banned.
 */
import { adjutantCompliance01 } from "../raid-disposition";
import { clampTrait01to100, judgmentNorm } from "./traits";

export type PolarPole = "low" | "mid" | "high";

/** Sell-demo poles: ≤20 low · ≥80 high · else mid. */
export function polarPole(axis: number): PolarPole {
  const v = clampTrait01to100(axis);
  if (v <= 20) return "low";
  if (v >= 80) return "high";
  return "mid";
}

/**
 * 직감↑: 급피 중 기회사격 포기 (생존 반사 · 딜 포기).
 * 직감↓: false → 발 밖이면 기회사격 유지 (맞고 싸움).
 */
export function instinctAbandonFireWhileDodge(instinct: number): boolean {
  return polarPole(instinct) === "high";
}

/**
 * DangerZone / telegraph T0 급피 시도 허용.
 * polar low (instinct ≤20, 포함 0) → 시도 없음 (`tank_hits_for_uptime`).
 * Soft stack speedMul은 Soft 바닥(비재능) — 이 게이트와 무관.
 */
export function instinctAllowsAoEDodge(instinct: number): boolean {
  return polarPole(instinct) !== "low";
}

/**
 * Option A 직감: chew/low-HP **자리 깨기 개방** (pack-escape + survival tip).
 * polar low → false (bias 0). Soft≪AA clear는 tip보다 우선.
 * Seat *preference* is insight (`survivalSeekSeatBonus`) — do not wire cover on instinct alone.
 */
export function instinctAllowsSeatBreak(instinct: number): boolean {
  return polarPole(instinct) !== "low";
}

/**
 * @deprecated Reserved stub — **do not wire alone**.
 * Prefer {@link instinctAllowsSeatBreak} via `survivalSeekScoreBonus`.
 */
export function instinctAllowsCoverSeek(instinct: number): boolean {
  return instinctAllowsSeatBreak(instinct);
}

/**
 * 통찰↑: 표적 재평가 망설임 (자리·타이밍 읽느라 윈도 놓침).
 * Returns multiplier on minSwitchGain (≥1). mid/low = 1.
 */
export function insightRetargetHesitateMul(insight: number): number {
  const t = Math.max(0, judgmentNorm(insight));
  return 1 + t * 0.85;
}

/**
 * 유대↑: 부관 시드 과준수 pull (인형극 아님 · Soft clear 재투영 후).
 * Base compliance×0.7; high bond → up to ×0.95.
 */
export function bondOverCompliancePull01(bond: number): number {
  const compliance = adjutantCompliance01(bond);
  if (compliance < 0.05) return 0;
  const over = Math.max(0, judgmentNorm(bond));
  return Math.min(0.95, compliance * (0.7 + 0.28 * over));
}

/** Human-readable failure mode ids for telemetry / Sim Lab copy. */
export function polarFailureModeId(
  axis: "instinct" | "insight" | "bond",
  value: number,
): string | null {
  const pole = polarPole(value);
  if (pole === "mid") return null;
  if (axis === "instinct") {
    return pole === "high"
      ? "abandon_dps_for_dodge"
      : "tank_hits_for_uptime";
  }
  if (axis === "insight") {
    return pole === "high"
      ? "hesitate_miss_window"
      : "simple_execute_mistake";
  }
  return pole === "high" ? "over_comply_seed" : "ignore_adjutant_call";
}
